import {
  BarChart3,
  BookOpen,
  Bot,
  Braces,
  Code,
  CreditCard,
  FileText,
  KeyRound,
  MessageCircle,
  ShieldCheck,
  Sparkles,
  SquareAsterisk,
  Users,
  Webhook,
} from 'lucide-react'
import { useMemo } from 'react'
import { useOnboardingSheet } from '@/components/onboarding-sheet/context'
import { usePaymentMethodGate } from '@/components/payment-method-gate/context'
import { useIdCompany, useMixpanel, useRBAC } from '@/lib/hooks'
import { RoleID } from '@/lib/rbac'
import type { SidebarPopoverItem } from '@/components/layout/sidebar-popover-menu'

export const useToolsItems = (): SidebarPopoverItem[] => {
  const { idCompany } = useIdCompany()
  const { rbac } = useRBAC()
  const isPaymentGated = usePaymentMethodGate()
  const companyRbac = useMemo(() => rbac.company(idCompany), [idCompany, rbac])

  return useMemo<SidebarPopoverItem[]>(
    () =>
      (
        [
          {
            title: 'Global variables',
            url: '/global-variables',
            icon: Braces,
            hasAccess: companyRbac.apps.view.can(),
          },
          {
            title: 'API keys',
            url: '/api-keys',
            icon: KeyRound,
            hasAccess: companyRbac.apiKeys.view.can(),
          },
          {
            title: 'Webhooks',
            url: '/webhooks',
            icon: Webhook,
            hasAccess:
              companyRbac.apps.create.can() &&
              companyRbac.staticSites.create.can() &&
              companyRbac.databases.create.can() &&
              companyRbac.objectStorages.create.can(),
          },
          {
            title: 'Registry credentials',
            url: '/registry-credentials',
            icon: SquareAsterisk,
            hasAccess: companyRbac.apps.view.can(),
          },
        ] as const
      ).map((item) => ({
        ...item,
        hasAccess: isPaymentGated ? false : item.hasAccess,
      })),
    [companyRbac, isPaymentGated]
  )
}

export const useSettingsItems = (): SidebarPopoverItem[] => {
  const { idCompany } = useIdCompany()
  const { rbac } = useRBAC()
  const isPaymentGated = usePaymentMethodGate()
  const companyRbac = useMemo(() => rbac.company(idCompany), [idCompany, rbac])

  return useMemo<SidebarPopoverItem[]>(
    () =>
      (
        [
          {
            title: 'Billing',
            url: '/billing',
            icon: CreditCard,
            hasAccess: companyRbac.billing.view.can(),
          },
          {
            title: 'Usage',
            url: '/usage',
            icon: BarChart3,
            hasAccess: companyRbac.usage.view.can(),
            isActive: (pathname: string) =>
              pathname.includes('/usage') && !pathname.includes('/project'),
          },
          {
            title: 'User management',
            url: '/user-management',
            icon: Users,
            hasAccess: companyRbac.users.view.can(),
          },
          {
            title: 'Single sign-on',
            url: '/single-sign-on',
            icon: ShieldCheck,
            hasAccess: companyRbac.users.update.can(),
          },
        ] as const
      ).map((item) => ({
        ...item,
        hasAccess:
          isPaymentGated && item.title !== 'Billing' ? false : item.hasAccess,
      })),
    [companyRbac, isPaymentGated]
  )
}

export const useHelpItems = (): SidebarPopoverItem[] => {
  const { rbac, rolesInCompany } = useRBAC()
  const { open } = useOnboardingSheet()
  const { track } = useMixpanel()
  const hasAiSupportAccess =
    rbac.kinsta().superadmin.can() ||
    rolesInCompany.some(
      (role) => role === RoleID.COMPANY_OWNER || role === RoleID.COMPANY_ADMIN
    )

  return useMemo<SidebarPopoverItem[]>(
    () => [
      {
        title: 'Get started guides',
        icon: Sparkles,
        hasAccess: true,
        action: true,
        onClick: () => {
          track('Onboarding guides visited', { entrypoint: 'help' })
          open(undefined, 'help')
        },
      },
      {
        title: 'AI support',
        url: '/help/ai-support',
        icon: Bot,
        hasAccess: hasAiSupportAccess,
      },
      {
        title: 'Platform docs',
        url: 'https://docs.sevalla.com',
        icon: BookOpen,
        hasAccess: true,
        external: true,
      },
      {
        title: 'API docs',
        url: 'https://api-docs.sevalla.com',
        icon: Code,
        hasAccess: true,
        external: true,
      },
      {
        title: 'Changelog',
        url: 'https://docs.sevalla.com/changelog',
        icon: FileText,
        hasAccess: true,
        external: true,
      },
      {
        title: 'Discord',
        url: 'https://discord.gg/sevalla',
        icon: MessageCircle,
        hasAccess: true,
        external: true,
      },
    ],
    [hasAiSupportAccess, open, track]
  )
}
